'use client'

import {
  Account,
  CreateAccountInput,
  CreateExpenseInput,
  CreateIncomeInput,
  Expense,
  Income,
  TrialBalance,
} from "./types/financial.types"
import {
  ApiEnvelope,
  CompletePasswordlessLoginInput,
  GoogleLoginInput,
  PasswordlessCompleteData,
  RequestPasswordlessLoginInput,
  UpdateMyProfileInput,
  UserProfile,
  AuthResponse,
} from "./types/auth.types"
import {
  AvailableSlotsRangeResponse,
  AvailableSlotsResponse,
  Appointment,
  AppointmentAvailabilityResponse,
  ApproveCustomAppointmentInput,
  BookingGroup,
  CancelAppointmentInput,
  CheckAppointmentAvailabilityInput,
  BookingGroupFilters,
  CheckGroupAvailabilityInput,
  CreateAppointmentInput,
  CreateGroupAppointmentInput,
  GroupAvailabilityResponse,
  UpdateAppointmentInput,
  UpdateAppointmentStatusInput,
} from "./types/appointment.types"
import { CreateHairRecordInput, HairRecord } from "./types/hair-record.types"
import type {
  ContentItemAdmin,
  CreateContentItemInput,
  PaginatedContentAdmin,
  UpdateContentItemInput,
} from "./types/content.types"
import {
  BusinessSettings,
  EmailSettings,
  FinancialSettings,
  GeneralSettings,
  GeoLocationSettings,
  NotificationSettings,
  PaymentSettings,
  PublicPaymentSettings,
  PricingSettings,
  SmsSettings,
  UpdateGeoLocationInput,
} from "./types/settings.types"
import type {
  PaginatedNotifications,
  UnreadNotificationCount,
  UserNotification,
} from "./types/notification.types"
import { CreateLocationInput, Location, LocationList } from "./types/location.types"
import {
  CreateServiceCategoryInput,
  CreateServiceInput,
  Service,
  ServiceCategory,
  ServiceDeletionEligibility,
  UpdateServiceCategoryInput,
  UpdateServiceInput,
} from "./types/service.types"
import NetworkUtils, { QueryParams } from "./network"
import {
  appointmentAvailableSlotsEndpoint,
  appointmentCheckAvailabilityEndpoint,
  appointmentApproveEndpoint,
  appointmentByIdEndpoint,
  appointmentCancelEndpoint,
  appointmentGroupByIdEndpoint,
  appointmentGroupCancelEndpoint,
  appointmentGroupsEndpoint,
  appointmentGroupCheckAvailabilityEndpoint,
  appointmentGroupEndpoint,
  appointmentStatusEndpoint,
  appointmentsEndpoint,
  authEndpoint,
  dashboardOverviewEndpoint,
  customerHairRecordsEndpoint,
  usersEndpoint,
  userByIdEndpoint,
  createCustomerEndpoint,
  staffEndpoint,
  staffInviteEndpoint,
  staffPromoteEndpoint,
  staffDemoteEndpoint,
  staffByIdEndpoint,
  reportsExportEndpoint,
  reportsDataEndpoint,
  financialsEndpoints,
  hairRecordsEndpoint,
  locationByIdEndpoint,
  locationEndpoint,
  mailEndpoints,
  notificationReadEndpoint,
  notificationsMeEndpoint,
  notificationsReadAllEndpoint,
  notificationsUnreadCountEndpoint,
  paystackInitiateCardPaymentEndpoint,
  paystackInitiateBankTransferEndpoint,
  paystackQuoteEndpoint,
  paystackVerifyEndpoint,
  serviceByIdEndpoint,
  serviceBySlugEndpoint,
  serviceCategoriesEndpoint,
  serviceCategoryByIdEndpoint,
  serviceCategoriesReorderEndpoint,
  serviceDeletionEligibilityEndpoint,
  serviceHardDeleteEndpoint,
  serviceRestoreEndpoint,
  servicesEndpoint,
  servicesReorderEndpoint,
  settingsEndpoints,
  contentEndpoints,
  dataBackupEndpoints,
  uploadByIdEndpoint,
  uploadsEndpoint,
  userProfileEndpoint,
  walletEndpoint,
  walletMeEndpoint,
  walletMeTransactionsEndpoint,
  walletMeWithdrawalsEndpoint,
  walletWithdrawalsListEndpoint,
  walletWithdrawalByIdEndpoint,
  walletUserCreditEndpoint,
  walletUserTransactionsEndpoint,
  myHairRecordsEndpoint,
  discountsEndpoint,
  discountByIdEndpoint,
  discountValidateEndpoint,
  surchargesEndpoint,
  surchargeByIdEndpoint,
  appointmentAdjustmentsEndpoint,
  appointmentDiscountAdjustmentEndpoint,
  appointmentSurchargeAdjustmentEndpoint,
  appointmentGoodwillAdjustmentEndpoint,
  appointmentReverseAdjustmentEndpoint,
  appointmentOverrideTotalEndpoint,
} from "./endpoints"

import type {
  Discount,
  CreateDiscountInput,
  UpdateDiscountInput,
  ValidateDiscountResponse,
  Surcharge,
  CreateSurchargeInput,
  UpdateSurchargeInput,
  AppointmentAdjustment,
  ApplyDiscountAdjustmentInput,
  ApplySurchargeAdjustmentInput,
  ApplyGoodwillAdjustmentInput,
  OverrideTotalInput,
} from "./types/discount.types"

import { Paginated } from "./types/common.types"
import type { DashboardOverview } from "./types/dashboard.types"
import type {
  AdminUpdateUserInput,
  DemoteStaffInput,
  InviteStaffInput,
  PromoteStaffInput,
  Staff,
  StaffList,
  UpdateStaffInput,
  User as ApiUser,
} from "./types/user.types"
import {
  InitiateBankTransferRequest,
  InitiateBankTransferResponse,
  InitiateCardPaymentRequest,
  InitiateCardPaymentResponse,
  QuotePaymentRequest,
  QuotePaymentResponse,
  VerifyPaymentResponse,
} from "./types/payment.types"
import { WalletList } from "./types/user.types"
import type {
  CreateWithdrawalInput,
  CreditWalletInput,
  ProcessWithdrawalInput,
  WalletTransaction,
  WalletTransactionList,
  WalletWithdrawalList,
  WalletWithdrawalRequest,
  WalletWithUser,
} from "./types/wallet.types"

// API service class with typed methods for the Melvoly Hair backend
export class ApiService {
  // Auth

  static async requestPasswordlessLogin(
    input: RequestPasswordlessLoginInput,
  ): Promise<ApiEnvelope<{ code?: string }>> {
    return NetworkUtils.post<ApiEnvelope<{ code?: string }>, RequestPasswordlessLoginInput>(
      `${authEndpoint}/passwordless/request`,
      input,
    )
  }

  static async completePasswordlessLogin(
    input: CompletePasswordlessLoginInput,
  ): Promise<ApiEnvelope<PasswordlessCompleteData>> {
    return NetworkUtils.post<ApiEnvelope<PasswordlessCompleteData>, CompletePasswordlessLoginInput>(
      `${authEndpoint}/passwordless/complete`,
      input,
    )
  }

  static async loginWithGoogle(
    input: GoogleLoginInput,
  ): Promise<AuthResponse | ApiEnvelope<PasswordlessCompleteData>> {
    return NetworkUtils.post<AuthResponse | ApiEnvelope<PasswordlessCompleteData>, GoogleLoginInput>(
      `${authEndpoint}/google`,
      input,
    )
  }

  static async getDashboardOverview(params?: {
    from?: string
    to?: string
  }): Promise<DashboardOverview> {
    const query: QueryParams = {
      from: params?.from,
      to: params?.to,
    }
    return NetworkUtils.get<DashboardOverview>(dashboardOverviewEndpoint, query)
  }

  static async getUsers(params?: {
    page?: number
    limit?: number
    role?: string
    search?: string
    status?: number
  }): Promise<Paginated<ApiUser>> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
      role: params?.role,
      search: params?.search,
      status: params?.status,
    }
    return NetworkUtils.get<Paginated<ApiUser>>(usersEndpoint, query)
  }

  static async getUser(id: number | string): Promise<ApiUser> {
    return NetworkUtils.get<ApiUser>(userByIdEndpoint(id))
  }

  static async createCustomer(input: {
    name: string
    email: string
    phone?: string
  }): Promise<ApiUser> {
    return NetworkUtils.post<ApiUser, { name: string; email: string; phone?: string }>(
      createCustomerEndpoint,
      input,
    )
  }

  static async adminUpdateUser(
    id: number | string,
    input: AdminUpdateUserInput,
  ): Promise<ApiUser> {
    return NetworkUtils.patch<ApiUser, AdminUpdateUserInput>(
      userByIdEndpoint(id),
      input,
    )
  }

  static async updateMyProfile(input: UpdateMyProfileInput): Promise<UserProfile> {
    return NetworkUtils.patch<UserProfile, UpdateMyProfileInput>(userProfileEndpoint, input)
  }

  static async getStaff(params?: {
    page?: number
    limit?: number
    search?: string
    isActive?: boolean
  }): Promise<StaffList> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      isActive: params?.isActive,
    }
    return NetworkUtils.get<StaffList>(staffEndpoint, query)
  }

  static async inviteStaff(input: InviteStaffInput): Promise<Staff> {
    return NetworkUtils.post<Staff, InviteStaffInput>(staffInviteEndpoint, input)
  }

  static async promoteStaff(input: PromoteStaffInput): Promise<Staff> {
    return NetworkUtils.post<Staff, PromoteStaffInput>(staffPromoteEndpoint, input)
  }

  static async demoteStaff(input: DemoteStaffInput): Promise<{ message: string; userId: number }> {
    return NetworkUtils.post<{ message: string; userId: number }, DemoteStaffInput>(
      staffDemoteEndpoint,
      input,
    )
  }

  static async updateStaff(id: number | string, input: UpdateStaffInput): Promise<Staff> {
    return NetworkUtils.patch<Staff, UpdateStaffInput>(staffByIdEndpoint(id), input)
  }

  static async downloadReport(
    reportKey: string,
    format: "csv" | "xlsx" | "pdf",
    params?: QueryParams,
  ): Promise<Blob> {
    return NetworkUtils.getBlob(reportsExportEndpoint(reportKey, format), params)
  }

  static async getReportData(
    reportKey: string,
    params?: QueryParams,
  ): Promise<{ title: string; headers: string[]; rows: (string | number)[][]; subtitle?: string }> {
    return NetworkUtils.get<{ title: string; headers: string[]; rows: (string | number)[][]; subtitle?: string }>(
      reportsDataEndpoint(reportKey),
      params,
    )
  }

  // Services

  static async getServices(params?: {
    page?: number
    limit?: number
    featured?: boolean
    search?: string
  }): Promise<Paginated<Service>> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
      featured: params?.featured,
      search: params?.search,
    }
    return NetworkUtils.get<Paginated<Service>>(servicesEndpoint, query)
  }

  static async getService(id: number | string): Promise<Service> {
    return NetworkUtils.get<Service>(serviceByIdEndpoint(id))
  }

  static async createService(input: CreateServiceInput): Promise<Service> {
    return NetworkUtils.post<Service, CreateServiceInput>(servicesEndpoint, input)
  }

  static async updateService(id: number | string, input: UpdateServiceInput): Promise<Service> {
    return NetworkUtils.patch<Service, UpdateServiceInput>(serviceByIdEndpoint(id), input)
  }

  static async softDeleteService(id: number | string): Promise<{ message: string }> {
    return NetworkUtils.delete<{ message: string }>(serviceByIdEndpoint(id))
  }

  static async getServiceDeletionEligibility(
    id: number | string,
  ): Promise<ServiceDeletionEligibility> {
    return NetworkUtils.get<ServiceDeletionEligibility>(
      serviceDeletionEligibilityEndpoint(id),
    )
  }

  static async hardDeleteService(id: number | string): Promise<{ message: string }> {
    return NetworkUtils.delete<{ message: string }>(serviceHardDeleteEndpoint(id))
  }

  // Service categories

  static async getServiceCategories(params?: {
    page?: number
    limit?: number
    name?: string
    slug?: string
    status?: number
  }): Promise<Paginated<ServiceCategory>> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
      name: params?.name,
      slug: params?.slug,
      status: params?.status,
    }
    return NetworkUtils.get<Paginated<ServiceCategory>>(serviceCategoriesEndpoint, query)
  }

  static async getServiceCategory(id: number | string): Promise<ServiceCategory> {
    return NetworkUtils.get<ServiceCategory>(serviceCategoryByIdEndpoint(id))
  }

  static async createServiceCategory(
    input: CreateServiceCategoryInput,
  ): Promise<ServiceCategory> {
    return NetworkUtils.post<ServiceCategory, CreateServiceCategoryInput>(
      serviceCategoriesEndpoint,
      input,
    )
  }

  static async updateServiceCategory(
    id: number | string,
    input: UpdateServiceCategoryInput,
  ): Promise<ServiceCategory> {
    return NetworkUtils.patch<ServiceCategory, UpdateServiceCategoryInput>(
      serviceCategoryByIdEndpoint(id),
      input,
    )
  }

  static async reorderServiceCategories(
    orderedIds: number[],
  ): Promise<ServiceCategory[]> {
    return NetworkUtils.patch<ServiceCategory[], { orderedIds: number[] }>(
      serviceCategoriesReorderEndpoint,
      { orderedIds },
    )
  }

  static async reorderServices(orderedIds: number[]): Promise<Service[]> {
    return NetworkUtils.patch<Service[], { orderedIds: number[] }>(
      servicesReorderEndpoint,
      { orderedIds },
    )
  }

  static async restoreService(id: number | string): Promise<Service> {
    return NetworkUtils.post<Service, undefined>(serviceRestoreEndpoint(id))
  }

  // Appointments

  static async getAppointments(params?: {
    page?: number
    limit?: number
    status?: string
    email?: string
    customerId?: string | number
    staffId?: string | number
    dateFrom?: string
    dateTo?: string
  }): Promise<Paginated<Appointment>> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
      status: params?.status,
      email: params?.email,
      customerId: params?.customerId,
      staffId: params?.staffId,
      dateFrom: params?.dateFrom,
      dateTo: params?.dateTo,
    }
    return NetworkUtils.get<Paginated<Appointment>>(appointmentsEndpoint, query)
  }

  static async getAvailableSlots(
    day: string,
    options?: { durationMinutes?: number; serviceIds?: number[] },
  ): Promise<AvailableSlotsResponse> {
    const query: QueryParams = {
      day,
      durationMinutes: options?.durationMinutes,
      serviceIds: options?.serviceIds?.length
        ? options.serviceIds.join(",")
        : undefined,
    }
    return NetworkUtils.get<AvailableSlotsResponse>(appointmentAvailableSlotsEndpoint, query)
  }

  static async getAvailableSlotsRange(
    daysFromToday = 90,
    options?: { durationMinutes?: number; serviceIds?: number[] },
  ): Promise<AvailableSlotsRangeResponse> {
    const query: QueryParams = {
      daysFromToday,
      durationMinutes: options?.durationMinutes,
      serviceIds: options?.serviceIds?.length
        ? options.serviceIds.join(",")
        : undefined,
    }
    return NetworkUtils.get<AvailableSlotsRangeResponse>(appointmentAvailableSlotsEndpoint, query)
  }

  static async getAppointment(id: number | string): Promise<Appointment> {
    return NetworkUtils.get<Appointment>(appointmentByIdEndpoint(id))
  }

  static async checkAppointmentAvailability(
    input: CheckAppointmentAvailabilityInput,
  ): Promise<AppointmentAvailabilityResponse> {
    return NetworkUtils.post<
      AppointmentAvailabilityResponse,
      CheckAppointmentAvailabilityInput
    >(appointmentCheckAvailabilityEndpoint, input)
  }

  static async createAppointment(input: CreateAppointmentInput): Promise<Appointment> {
    return NetworkUtils.post<Appointment, CreateAppointmentInput>(appointmentsEndpoint, input)
  }

  static async checkGroupAvailability(
    input: CheckGroupAvailabilityInput,
  ): Promise<GroupAvailabilityResponse> {
    return NetworkUtils.post<GroupAvailabilityResponse, CheckGroupAvailabilityInput>(
      appointmentGroupCheckAvailabilityEndpoint,
      input,
    )
  }

  static async createGroupAppointment(
    input: CreateGroupAppointmentInput,
  ): Promise<BookingGroup> {
    return NetworkUtils.post<BookingGroup, CreateGroupAppointmentInput>(
      appointmentGroupEndpoint,
      input,
    )
  }

  static async getBookingGroups(
    params?: BookingGroupFilters,
  ): Promise<Paginated<BookingGroup>> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
      status: params?.status,
      organizerUserId: params?.organizerUserId,
      dateFrom: params?.dateFrom,
      dateTo: params?.dateTo,
    }
    return NetworkUtils.get<Paginated<BookingGroup>>(appointmentGroupsEndpoint, query)
  }

  static async getBookingGroup(id: number | string): Promise<BookingGroup> {
    return NetworkUtils.get<BookingGroup>(appointmentGroupByIdEndpoint(id))
  }

  static async cancelBookingGroup(
    id: number | string,
    input: CancelAppointmentInput,
  ): Promise<BookingGroup> {
    return NetworkUtils.patch<BookingGroup, CancelAppointmentInput>(
      appointmentGroupCancelEndpoint(id),
      input,
    )
  }

  static async updateAppointment(
    id: number | string,
    input: UpdateAppointmentInput,
  ): Promise<Appointment> {
    return NetworkUtils.patch<Appointment, UpdateAppointmentInput>(
      appointmentByIdEndpoint(id),
      input,
    )
  }

  static async cancelAppointment(
    id: number | string,
    input: CancelAppointmentInput,
  ): Promise<Appointment> {
    return NetworkUtils.patch<Appointment, CancelAppointmentInput>(
      appointmentCancelEndpoint(id),
      input,
    )
  }

  static async updateAppointmentStatus(
    id: number | string,
    input: UpdateAppointmentStatusInput,
  ): Promise<Appointment> {
    return NetworkUtils.patch<Appointment, UpdateAppointmentStatusInput>(
      appointmentStatusEndpoint(id),
      input,
    )
  }

  static async approveCustomAppointment(
    id: number | string,
    input: ApproveCustomAppointmentInput,
  ): Promise<Appointment> {
    return NetworkUtils.post<Appointment, ApproveCustomAppointmentInput>(
      appointmentApproveEndpoint(id),
      input,
    )
  }

  static async createHairRecord(input: CreateHairRecordInput): Promise<HairRecord> {
    return NetworkUtils.post<HairRecord, CreateHairRecordInput>(hairRecordsEndpoint, input)
  }

  static async getMyHairRecords(): Promise<HairRecord[]> {
    return NetworkUtils.get<HairRecord[]>(myHairRecordsEndpoint)
  }

  static async getCustomerHairRecords(customerUserId: number | string): Promise<HairRecord[]> {
    return NetworkUtils.get<HairRecord[]>(customerHairRecordsEndpoint(customerUserId))
  }

  // Financials (partial)

  static async getAccounts(params?: {
    page?: number
    limit?: number
  }): Promise<Paginated<Account>> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
    }
    return NetworkUtils.get<Paginated<Account>>(financialsEndpoints.accounts, query)
  }

  static async createAccount(input: CreateAccountInput): Promise<Account> {
    return NetworkUtils.post<Account, CreateAccountInput>(financialsEndpoints.accounts, input)
  }

  static async updateAccount(
    id: number | string,
    input: CreateAccountInput,
  ): Promise<Account> {
    return NetworkUtils.patch<Account, CreateAccountInput>(
      `${financialsEndpoints.accounts}/${id}`,
      input,
    )
  }

  static async deleteAccount(id: number | string): Promise<{ message: string }> {
    return NetworkUtils.delete<{ message: string }>(`${financialsEndpoints.accounts}/${id}`)
  }

  static async getTrialBalance(asOfDate?: string): Promise<TrialBalance> {
    const query: QueryParams = {
      asOfDate,
    }
    return NetworkUtils.get<TrialBalance>(financialsEndpoints.reports.trialBalance, query)
  }

  static async getBalanceSheet(asOfDate?: string): Promise<unknown> {
    return NetworkUtils.get(financialsEndpoints.reports.balanceSheet, { asOfDate })
  }

  static async getIncomeStatement(startDate: string, endDate: string): Promise<unknown> {
    return NetworkUtils.get(financialsEndpoints.reports.incomeStatement, {
      startDate,
      endDate,
    })
  }

  static async getTaxSummary(startDate?: string, endDate?: string): Promise<unknown> {
    return NetworkUtils.get(financialsEndpoints.reports.taxSummary, {
      startDate,
      endDate,
    })
  }

  static async getIncomes(params?: {
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
  }): Promise<Paginated<Income>> {
    const query: QueryParams = {
      startDate: params?.startDate,
      endDate: params?.endDate,
      page: params?.page,
      limit: params?.limit,
    }
    return NetworkUtils.get<Paginated<Income>>(financialsEndpoints.income, query)
  }

  static async createIncome(input: CreateIncomeInput): Promise<Income> {
    return NetworkUtils.post<Income, CreateIncomeInput>(financialsEndpoints.income, input)
  }

  static async getExpenses(params?: {
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
  }): Promise<Paginated<Expense>> {
    const query: QueryParams = {
      startDate: params?.startDate,
      endDate: params?.endDate,
      page: params?.page,
      limit: params?.limit,
    }
    return NetworkUtils.get<Paginated<Expense>>(financialsEndpoints.expenses, query)
  }

  static async createExpense(input: CreateExpenseInput): Promise<Expense> {
    return NetworkUtils.post<Expense, CreateExpenseInput>(financialsEndpoints.expenses, input)
  }

  // Paystack card payment

  static async quotePayment(input: QuotePaymentRequest): Promise<QuotePaymentResponse> {
    return NetworkUtils.post<QuotePaymentResponse, QuotePaymentRequest>(
      paystackQuoteEndpoint,
      input,
    )
  }

  static async initiateCardPayment(
    input: InitiateCardPaymentRequest,
  ): Promise<InitiateCardPaymentResponse> {
    return NetworkUtils.post<InitiateCardPaymentResponse, InitiateCardPaymentRequest>(
      paystackInitiateCardPaymentEndpoint,
      input,
    )
  }

  static async initiateBankTransfer(
    input: InitiateBankTransferRequest,
  ): Promise<InitiateBankTransferResponse> {
    return NetworkUtils.post<InitiateBankTransferResponse, InitiateBankTransferRequest>(
      paystackInitiateBankTransferEndpoint,
      input,
    )
  }

  static async verifyPayment(reference: string): Promise<VerifyPaymentResponse> {
    return NetworkUtils.get<VerifyPaymentResponse>(paystackVerifyEndpoint, {
      reference,
    })
  }

  // Wallet

  static async getWallets(params?: {
    page?: number
    limit?: number
    search?: string
  }): Promise<WalletList> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
    }
    return NetworkUtils.get<WalletList>(walletEndpoint, query)
  }

  static async getMyWallet(): Promise<WalletWithUser> {
    return NetworkUtils.get<WalletWithUser>(walletMeEndpoint)
  }

  static async getMyWalletTransactions(params?: {
    page?: number
    limit?: number
  }): Promise<WalletTransactionList> {
    return NetworkUtils.get<WalletTransactionList>(walletMeTransactionsEndpoint, {
      page: params?.page,
      limit: params?.limit,
    })
  }

  static async creditUserWallet(
    userId: number,
    input: CreditWalletInput,
  ): Promise<WalletTransaction> {
    return NetworkUtils.post<WalletTransaction, CreditWalletInput>(
      walletUserCreditEndpoint(userId),
      input,
    )
  }

  static async getWalletWithdrawals(params?: {
    status?: string
    page?: number
    limit?: number
    search?: string
  }): Promise<WalletWithdrawalList> {
    return NetworkUtils.get<WalletWithdrawalList>(walletWithdrawalsListEndpoint, {
      status: params?.status,
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
    })
  }

  static async processWalletWithdrawal(
    id: number,
    input: ProcessWithdrawalInput,
  ): Promise<WalletWithdrawalRequest> {
    return NetworkUtils.patch<WalletWithdrawalRequest, ProcessWithdrawalInput>(
      walletWithdrawalByIdEndpoint(id),
      input,
    )
  }

  static async createMyWithdrawal(
    input: CreateWithdrawalInput,
  ): Promise<WalletWithdrawalRequest> {
    return NetworkUtils.post<WalletWithdrawalRequest, CreateWithdrawalInput>(
      walletMeWithdrawalsEndpoint,
      input,
    )
  }

  static async getMyWithdrawals(): Promise<WalletWithdrawalRequest[]> {
    return NetworkUtils.get<WalletWithdrawalRequest[]>(walletMeWithdrawalsEndpoint)
  }

  static async cancelMyWithdrawal(id: number): Promise<WalletWithdrawalRequest> {
    return NetworkUtils.delete<WalletWithdrawalRequest>(`${walletMeWithdrawalsEndpoint}/${id}`)
  }

  static async getServiceBySlug(slug: string): Promise<Service> {
    return NetworkUtils.get<Service>(serviceBySlugEndpoint(slug))
  }

  // Settings

  static async getGeneralSettings(): Promise<GeneralSettings> {
    return NetworkUtils.get<GeneralSettings>(settingsEndpoints.general)
  }

  static async updateGeneralSettings(input: GeneralSettings): Promise<GeneralSettings> {
    return NetworkUtils.post<GeneralSettings, GeneralSettings>(
      settingsEndpoints.general,
      input,
    )
  }

  static async getEmailSettings(): Promise<EmailSettings> {
    return NetworkUtils.get<EmailSettings>(settingsEndpoints.email)
  }

  static async updateEmailSettings(input: EmailSettings): Promise<EmailSettings> {
    return NetworkUtils.post<EmailSettings, EmailSettings>(settingsEndpoints.email, input)
  }

  static async getPaymentSettings(): Promise<PaymentSettings> {
    return NetworkUtils.get<PaymentSettings>(settingsEndpoints.payment)
  }

  static async getPublicPaymentSettings(): Promise<PublicPaymentSettings> {
    return NetworkUtils.get<PublicPaymentSettings>(settingsEndpoints.paymentPublic)
  }

  static async updatePaymentSettings(input: PaymentSettings): Promise<PaymentSettings> {
    return NetworkUtils.post<PaymentSettings, PaymentSettings>(
      settingsEndpoints.payment,
      input,
    )
  }

  static async getNotificationSettings(): Promise<NotificationSettings> {
    return NetworkUtils.get<NotificationSettings>(settingsEndpoints.notification)
  }

  static async updateNotificationSettings(
    input: NotificationSettings,
  ): Promise<NotificationSettings> {
    return NetworkUtils.post<NotificationSettings, NotificationSettings>(
      settingsEndpoints.notification,
      input,
    )
  }

  static async getMyNotifications(params?: {
    page?: number
    limit?: number
  }): Promise<PaginatedNotifications> {
    return NetworkUtils.get<PaginatedNotifications>(notificationsMeEndpoint, {
      page: params?.page,
      limit: params?.limit,
    })
  }

  static async getNotificationUnreadCount(): Promise<UnreadNotificationCount> {
    return NetworkUtils.get<UnreadNotificationCount>(
      notificationsUnreadCountEndpoint,
    )
  }

  static async markNotificationRead(id: number): Promise<UserNotification> {
    return NetworkUtils.patch<UserNotification>(notificationReadEndpoint(id))
  }

  static async markAllNotificationsRead(): Promise<{ updated: number }> {
    return NetworkUtils.patch<{ updated: number }>(notificationsReadAllEndpoint)
  }

  static async getFinancialSettings(): Promise<FinancialSettings> {
    return NetworkUtils.get<FinancialSettings>(settingsEndpoints.financial)
  }

  static async updateFinancialSettings(
    input: FinancialSettings,
  ): Promise<FinancialSettings> {
    return NetworkUtils.post<FinancialSettings, FinancialSettings>(
      settingsEndpoints.financial,
      input,
    )
  }

  static async getPricingSettings(): Promise<PricingSettings> {
    return NetworkUtils.get<PricingSettings>(settingsEndpoints.pricing)
  }

  static async updatePricingSettings(input: PricingSettings): Promise<PricingSettings> {
    return NetworkUtils.post<PricingSettings, PricingSettings>(
      settingsEndpoints.pricing,
      input,
    )
  }

  static async getGeoLocationSettings(): Promise<GeoLocationSettings> {
    return NetworkUtils.get<GeoLocationSettings>(settingsEndpoints.geoLocation)
  }

  static async updateGeoLocationSettings(
    input: UpdateGeoLocationInput,
  ): Promise<GeoLocationSettings> {
    return NetworkUtils.post<GeoLocationSettings, UpdateGeoLocationInput>(
      settingsEndpoints.geoLocation,
      input,
    )
  }

  static async getSmsSettings(): Promise<SmsSettings> {
    return NetworkUtils.get<SmsSettings>(settingsEndpoints.sms)
  }

  static async updateSmsSettings(input: SmsSettings): Promise<SmsSettings> {
    return NetworkUtils.post<SmsSettings, SmsSettings>(settingsEndpoints.sms, input)
  }

  static async getBusinessSettings(): Promise<BusinessSettings> {
    return NetworkUtils.get<BusinessSettings>(settingsEndpoints.business)
  }

  static async updateBusinessSettings(input: BusinessSettings): Promise<BusinessSettings> {
    return NetworkUtils.post<BusinessSettings, BusinessSettings>(
      settingsEndpoints.business,
      input,
    )
  }

  // Locations

  static async getLocations(params?: {
    page?: number
    limit?: number
  }): Promise<LocationList> {
    const query: QueryParams = {
      page: params?.page,
      limit: params?.limit,
    }
    return NetworkUtils.get<LocationList>(locationEndpoint, query)
  }

  static async getLocation(id: number | string): Promise<Location> {
    return NetworkUtils.get<Location>(locationByIdEndpoint(id))
  }

  static async createLocation(input: CreateLocationInput): Promise<Location> {
    return NetworkUtils.post<Location, CreateLocationInput>(locationEndpoint, input)
  }

  // Mails (basic endpoints, strongly typed payloads can be added later)

  static async listMailTemplates(params?: QueryParams): Promise<unknown> {
    return NetworkUtils.get<unknown>(mailEndpoints.templates, params)
  }

  static async listMailLogs(params?: QueryParams): Promise<unknown> {
    return NetworkUtils.get<unknown>(mailEndpoints.logs, params)
  }

  // Content

  static async listContentAdminItems(params?: {
    type?: string
    status?: string
    page?: number
    limit?: number
  }): Promise<PaginatedContentAdmin> {
    const query: QueryParams = {
      type: params?.type,
      status: params?.status,
      page: params?.page,
      limit: params?.limit,
    }
    return NetworkUtils.get<PaginatedContentAdmin>(contentEndpoints.adminItems, query)
  }

  static async getContentAdminItem(id: number | string): Promise<ContentItemAdmin> {
    return NetworkUtils.get<ContentItemAdmin>(contentEndpoints.adminItemById(id))
  }

  static async createContentItem(input: CreateContentItemInput): Promise<ContentItemAdmin> {
    return NetworkUtils.post<ContentItemAdmin, CreateContentItemInput>(
      contentEndpoints.adminItems,
      input,
    )
  }

  static async updateContentItem(
    id: number | string,
    input: UpdateContentItemInput,
  ): Promise<ContentItemAdmin> {
    return NetworkUtils.patch<ContentItemAdmin, UpdateContentItemInput>(
      contentEndpoints.adminItemById(id),
      input,
    )
  }

  static async deleteContentItem(id: number | string): Promise<{ success: boolean }> {
    return NetworkUtils.delete<{ success: boolean }>(contentEndpoints.adminItemById(id))
  }

  // Uploads

  static async getUpload(id: number | string): Promise<unknown> {
    return NetworkUtils.get<unknown>(uploadByIdEndpoint(id))
  }

  static async uploadFile(
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<unknown> {
    return NetworkUtils.uploadFile<unknown>(uploadsEndpoint, file, onProgress)
  }

  // Business data backup

  static async exportBusinessData(regions: string[]): Promise<Blob> {
    const regionsParam =
      regions.length === 0 || regions.includes("all")
        ? "all"
        : regions.join(",")
    return NetworkUtils.getBlob(
      dataBackupEndpoints.export,
      { regions: regionsParam },
      { timeout: 300_000 },
    )
  }

  static async importBusinessData(file: File): Promise<{
    regions: string[]
    counts: Record<string, number>
    message: string
  }> {
    const formData = new FormData()
    formData.append("file", file)
    return NetworkUtils.post(dataBackupEndpoints.import, formData, {
      timeout: 300_000,
      headers: { "Content-Type": "multipart/form-data" },
    })
  }

  // Discounts

  static async getDiscounts(params?: {
    status?: string
    type?: string
  }): Promise<Discount[]> {
    return NetworkUtils.get<Discount[]>(discountsEndpoint, params as QueryParams)
  }

  static async getDiscountById(id: number | string): Promise<Discount> {
    return NetworkUtils.get<Discount>(discountByIdEndpoint(id))
  }

  static async createDiscount(input: CreateDiscountInput): Promise<Discount> {
    return NetworkUtils.post<Discount, CreateDiscountInput>(discountsEndpoint, input)
  }

  static async updateDiscount(
    id: number | string,
    input: UpdateDiscountInput,
  ): Promise<Discount> {
    return NetworkUtils.patch<Discount, UpdateDiscountInput>(discountByIdEndpoint(id), input)
  }

  static async deleteDiscount(id: number | string): Promise<void> {
    return NetworkUtils.delete<void>(discountByIdEndpoint(id))
  }

  static async validateDiscount(input: {
    code: string
    serviceIds?: number[]
    appointmentId?: number
  }): Promise<ValidateDiscountResponse> {
    return NetworkUtils.post<ValidateDiscountResponse, typeof input>(
      discountValidateEndpoint,
      input,
    )
  }

  // Surcharges

  static async getSurcharges(status?: string): Promise<Surcharge[]> {
    return NetworkUtils.get<Surcharge[]>(
      surchargesEndpoint,
      (status ? { status } : undefined) as QueryParams,
    )
  }

  static async getSurchargeById(id: number | string): Promise<Surcharge> {
    return NetworkUtils.get<Surcharge>(surchargeByIdEndpoint(id))
  }

  static async createSurcharge(input: CreateSurchargeInput): Promise<Surcharge> {
    return NetworkUtils.post<Surcharge, CreateSurchargeInput>(surchargesEndpoint, input)
  }

  static async updateSurcharge(
    id: number | string,
    input: UpdateSurchargeInput,
  ): Promise<Surcharge> {
    return NetworkUtils.patch<Surcharge, UpdateSurchargeInput>(surchargeByIdEndpoint(id), input)
  }

  static async deleteSurcharge(id: number | string): Promise<void> {
    return NetworkUtils.delete<void>(surchargeByIdEndpoint(id))
  }

  // Appointment Adjustments

  static async getAppointmentAdjustments(
    appointmentId: number | string,
  ): Promise<AppointmentAdjustment[]> {
    return NetworkUtils.get<AppointmentAdjustment[]>(
      appointmentAdjustmentsEndpoint(appointmentId),
    )
  }

  static async applyDiscountAdjustment(
    appointmentId: number | string,
    input: ApplyDiscountAdjustmentInput,
  ): Promise<Appointment> {
    return NetworkUtils.post<Appointment, ApplyDiscountAdjustmentInput>(
      appointmentDiscountAdjustmentEndpoint(appointmentId),
      input,
    )
  }

  static async applySurchargeAdjustment(
    appointmentId: number | string,
    input: ApplySurchargeAdjustmentInput,
  ): Promise<Appointment> {
    return NetworkUtils.post<Appointment, ApplySurchargeAdjustmentInput>(
      appointmentSurchargeAdjustmentEndpoint(appointmentId),
      input,
    )
  }

  static async applyGoodwillAdjustment(
    appointmentId: number | string,
    input: ApplyGoodwillAdjustmentInput,
  ): Promise<Appointment> {
    return NetworkUtils.post<Appointment, ApplyGoodwillAdjustmentInput>(
      appointmentGoodwillAdjustmentEndpoint(appointmentId),
      input,
    )
  }

  static async reverseAppointmentAdjustment(
    appointmentId: number | string,
    adjustmentId: number | string,
  ): Promise<Appointment> {
    return NetworkUtils.post<Appointment, Record<string, never>>(
      appointmentReverseAdjustmentEndpoint(appointmentId, adjustmentId),
      {},
    )
  }

  static async overrideAppointmentTotal(
    appointmentId: number | string,
    input: OverrideTotalInput,
  ): Promise<Appointment> {
    return NetworkUtils.post<Appointment, OverrideTotalInput>(
      appointmentOverrideTotalEndpoint(appointmentId),
      input,
    )
  }
}
